/**
 * Utility functions for calculating pub ratings based on sunlight data
 */

/**
 * Calculates a beer mug rating (1-5) based on sun point hours
 * @param pointHours - The total sun point hours for a pub's garden(s)
 * @returns A rating from 1 to 5 beer mugs
 */
export const calculateBeerMugRating = (pointHours: number): number => {
    const ratingThresholds = { 1: 10, 2: 500, 3: 2000, 4: 10000, 5: 20000 };
    let rating = 1;
    for (let r = 5; r > 0; r--) {
        if (pointHours >= ratingThresholds[r as keyof typeof ratingThresholds]) {
            rating = r;
            break;
        }
    }
    return rating;
};

/**
 * Calculates the current sun intensity (0-1) for a pub based on its heatmap data
 * @param heatmap - The heatmap data with timestamps and values
 * @returns A value from 0 to 1 representing current sun intensity (0 = no sun, 1 = full sun)
 */
export const getCurrentSunIntensity = (heatmap: { timestamps: string[]; values: number[] } | undefined): number => {
    if (!heatmap || !heatmap.timestamps || !Array.isArray(heatmap.timestamps) || !heatmap.values || !Array.isArray(heatmap.values)) {
        return 0;
    }
    
    if (heatmap.timestamps.length === 0 || heatmap.values.length === 0) {
        return 0;
    }
    
    const now = new Date();
    const timestamps = heatmap.timestamps;
    const values = heatmap.values;
    
    // Get current time in minutes from midnight (ignoring date)
    const currentMinutes = now.getHours() * 60 + now.getMinutes();
    
    // Find the nearest timestamp by comparing minutes from midnight only (ignoring date)
    let nearestIndex = 0;
    let minMinutesDiff = Infinity;
    
    timestamps.forEach((timestamp, index) => {
        try {
            const timestampDate = new Date(timestamp);
            
            // Get this time in minutes from midnight
            const timeMinutes = timestampDate.getHours() * 60 + timestampDate.getMinutes();
            
            // Calculate difference, handling the wrap around midnight case
            let minutesDiff = Math.abs(timeMinutes - currentMinutes);
            // Consider times that wrap around midnight (e.g. 23:50 vs 00:10)
            minutesDiff = Math.min(minutesDiff, 1440 - minutesDiff);
            
            if (minutesDiff < minMinutesDiff) {
                minMinutesDiff = minutesDiff;
                nearestIndex = index;
            }
        } catch (e) {
            // Just skip problematic timestamps
        }
    });
    
    // Make sure the index is in bounds
    if (nearestIndex >= values.length) {
        return 0;
    }
    
    // Get the current value (0-1 percentage)
    const currentValue = typeof values[nearestIndex] === 'number' ? values[nearestIndex] : 0;
    
    // Ensure the value is within bounds
    return Math.max(0, Math.min(1, currentValue));
};

/**
 * Categorizes garden size based on area value
 * @param area - The garden area in square meters
 * @returns A size category string
 */
export const categorizeGardenSize = (area: number): string => {
    if (area <= 100) {
        return "small";
    } else if (area <= 300) {
        return "medium";
    } else if (area <= 1000) {
        return "large";
    } else {
        return "very large";
    }
}; 